import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Lock, ArrowRight, X } from 'lucide-react';
import Button from './Button';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: string;
  message?: string;
}

export default function UpgradePrompt({ isOpen, onClose, limitType, message }: UpgradePromptProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div data-testid="modal-backdrop" className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-96 max-w-[90vw] bg-bg-elevated border border-border-default rounded-xl shadow-2xl overflow-hidden">
        {/* Accent line */}
        <div className="h-1 bg-gradient-to-r from-accent-primary via-accent-secondary to-accent-primary" />

        <div className="p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-accent-primary/20 flex items-center justify-center">
              <Lock className="w-6 h-6 text-accent-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                {limitType}
              </h3>
              <p className="text-sm text-text-secondary">
                {t('upgradePrompt.title')}
              </p>
            </div>
          </div>

          <p className="text-sm text-text-secondary mb-6">
            {message || t('upgradePrompt.message')}
          </p>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-text-secondary hover:text-text-primary bg-bg-tertiary hover:bg-bg-secondary rounded-lg transition-colors"
            >
              {t('actions.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={handleUpgrade}
              className="flex-1 gap-2"
            >
              {t('actions.upgradePlan')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
